import {Cache, Matrix, Particle, ParticleContainer, Texture} from 'pixi.js';
import {disableCulling} from '../utils/PIXICuller.js';
import {DAMAGE_PARTS} from './remoteDamage.js';
import {WOUND_FLAGS, woundAngle} from './woundProtocol.js';
import {getCharacterBloodColors} from '../utils/BloodColors.js';
import {DEFAULT_BLOOD_COLOR, rainbowBloodColor} from '../../static/mod/bloodColors.js';
import {neutralBloodTexture} from '../utils/bloodTexture.js';

export const REMOTE_GORE_LIMITS = Object.freeze({particles: 192, perFrame: 64, perSecond: 240, wounds: 12});
const organs = {
	head: ['Gore_Brain0000'],
	body: ['Gore_LungRight0000', 'Gore_LungLeft0000', 'Gore_Stomach0000', 'Gore_Liver0000'],
	belly: ['Gore_Intestine0000'],
};
const clampSpeed = value => Math.max(-700, Math.min(700, value || 0));

// Cosmetic, world-space effects shared by ALL peers. No Box2D bodies, terrain
// queries, decals or particle packets. Each texture source gets one GPU batch.
export class RemoteGore {
	constructor() {
		this.active = [];
		this.free = [];
		this.wounds = [];
		this.freeWounds = [];
		this.batches = new Map();
		this.textures = new Map();
		this.transform = new Matrix();
		this.tokens = REMOTE_GORE_LIMITS.perFrame;
		this.remaining = 0;
	}

	beginFrame(parent, now, settings, width, height, gravityX = 0, gravityY = 600) {
		const elapsed = this.lastTime == null ? 0 : Math.max(0, (now - this.lastTime) / 1000);
		if (this.parent !== parent || parent?.destroyed || elapsed > 0.5) this.clear();
		this.parent = parent?.destroyed ? null : parent;
		this.lastTime = now;
		this.enabled = !!settings.goreEnabled && !!this.parent;
		this.rainbow = !settings.bloodEnabled;
		this.width = width;
		this.height = height;
		this.gravityX = gravityX;
		this.gravityY = gravityY;
		this.remaining = REMOTE_GORE_LIMITS.perFrame;
		this.tokens = Math.min(REMOTE_GORE_LIMITS.perFrame, this.tokens + elapsed * REMOTE_GORE_LIMITS.perSecond);
		this.delta = Math.min(elapsed, 0.1);
		// Fetch the actual scene matrix once, including the camera exactly once.
		if (this.enabled) this.parent.getGlobalTransform(this.transform);
		if (!this.enabled && (this.active.length || this.wounds.length)) this.clear();
	}

	isVisible(x, y) {
		const m = this.transform;
		const sx = m.a * x + m.c * y + m.tx;
		const sy = m.b * x + m.d * y + m.ty;
		return this.enabled && sx >= -100 && sy >= -100 && sx <= this.width + 100 && sy <= this.height + 100;
	}

	spawn(owner, name, kind, x, y, vx, vy) {
		if (!this.enabled || this.remaining <= 0 || this.tokens < 1 || this.active.length >= REMOTE_GORE_LIMITS.particles) return false;
		let texture = this.textures.get(name);
		if (!texture) {
			if (!Cache.has(name)) return false;
			texture = Texture.from(name);
			this.textures.set(name, texture);
		}
		const colors = owner.bloodColors || getCharacterBloodColors(owner.skin);
		const color = this.rainbow ? rainbowBloodColor(this.lastTime / 1000) : colors.start;
		const recolorCloud = kind === 'cloud' && (this.rainbow || color !== DEFAULT_BLOOD_COLOR);
		if (recolorCloud) texture = neutralBloodTexture(texture);
		let batch = this.batches.get(texture.source);
		if (!batch) {
			batch = new ParticleContainer({dynamicProperties: {position: true, rotation: true, color: true, vertex: true, uvs: false}});
			batch.label = 'remote-gore';
			batch.eventMode = 'none';
			disableCulling(batch);
			this.batches.set(texture.source, batch);
		}
		if (batch.parent !== this.parent) this.parent.addChild(batch);
		const p = this.free.pop() || new Particle(texture);
		p.texture = texture;
		p.anchorX = p.anchorY = 0.5;
		p.x = x;
		p.y = y;
		p.rotation = kind === 'blood' ? Math.atan2(vy, vx) : Math.random() * Math.PI * 2;
		p.tint = kind === 'blood' || recolorCloud ? parseInt(color.slice(1), 16) : 0xffffff;
		p.alpha = kind === 'cloud' ? 0.7 : 1;
		p.scaleX = p.scaleY = kind === 'blood' ? (4 + Math.random() * 4) / texture.width : kind === 'cloud' ? 0.4 : 0.65 + Math.random() * 0.5;
		p.baseScale = p.scaleX;
		p.owner = owner;
		p.kind = kind;
		p.age = 0;
		p.life = kind === 'cloud' ? 0.28 : kind === 'blood' ? 0.5 + Math.random() * 0.35 : 0.7 + Math.random() * 0.55;
		p.vx = vx;
		p.vy = vy;
		p.spin = kind === 'chunk' ? (Math.random() - 0.5) * 16 : 0;
		p.batch = batch;
		p.trailAt = 0.08;
		batch.addParticle(p);
		this.active.push(p);
		this.remaining--;
		this.tokens--;
		return true;
	}

	burst(owner, part, x, y, vx = 0, vy = 0, severed = false) {
		if (!this.isVisible(x, y) || this.remaining <= 0 || this.tokens < 1 || this.active.length >= REMOTE_GORE_LIMITS.particles) return false;
		vx = clampSpeed(vx) * 0.65;
		vy = clampSpeed(vy) * 0.65;
		let emitted = false;
		if (!severed) {
			const extras = organs[part];
			const count = part === 'body' ? 9 : part === 'head' ? 6 : 3;
			for (let i = 0; i < count; i++) {
				const name = extras?.[i] || `Gore_Meat${1 + (Math.random() * 6 | 0)}0000`;
				emitted = this.spawn(owner, name, 'chunk', x, y, vx + (Math.random() - 0.5) * 460, vy - 80 + (Math.random() - 0.5) * 400) || emitted;
			}
			emitted = this.spawn(owner, 'gore-cloud.png', 'cloud', x, y, vx * 0.1, vy * 0.1) || emitted;
		}
		for (let i = 0; i < (severed ? 8 : 16); i++) {
			emitted = this.spawn(owner, 'particle.png', 'blood', x, y, vx + (Math.random() - 0.5) * 600, vy + (Math.random() - 0.5) * 500) || emitted;
		}
		return emitted;
	}

	bleed(owner, key) {
		if (!this.enabled || this.wounds.length >= REMOTE_GORE_LIMITS.wounds || this.wounds.some(w => w.owner === owner && w.key === key)) return;
		const state = owner.state[key];
		if (!this.isVisible(owner.state.body.x + (key === 'body' ? 0 : state.x), owner.state.body.y + (key === 'body' ? 0 : state.y))) return;
		const wound = this.freeWounds.pop() || {};
		Object.assign(wound, {owner, key, life: 2.3, timer: 0, hit: null});
		this.wounds.push(wound);
	}

	// Texture-local impact coordinates keep sprays attached through animation,
	// flips and camera motion. Ordinary hits use this too, without destroying a limb.
	spray(owner, hit) {
		if (!this.enabled) return;
		const key = DAMAGE_PARTS[hit.part];
		const sprite = owner.sprites?.[key];
		if (!sprite?.visible || owner.state[key].destroyed) return;
		let wound = this.wounds.find(w => w.owner === owner && w.key === key);
		if (!wound) {
			if (this.wounds.length >= REMOTE_GORE_LIMITS.wounds) return;
			wound = this.freeWounds.pop() || {};
			this.wounds.push(wound);
		}
		Object.assign(wound, {owner, key, hit, life: (hit.flags & WOUND_FLAGS.LONG_SPRAY) ? 2.3 : (hit.flags & WOUND_FLAGS.CARVE) ? 0.9 : 0.45, timer: 0});
		this.emitSpray(wound, 16);
	}

	emitSpray(wound, count) {
		const {owner, key, hit} = wound;
		const sprite = owner.sprites?.[key];
		if (!sprite) return;
		let x, y, direction;
		if (hit) {
			const localX = (hit.x / 4 - sprite.anchor.x * sprite.texture.width) * sprite.scale.x;
			const localY = (hit.y / 4 - sprite.anchor.y * sprite.texture.height) * sprite.scale.y;
			const c = Math.cos(sprite.rotation), s = Math.sin(sprite.rotation);
			x = owner.sprite.x + sprite.x + localX * c - localY * s;
			y = owner.sprite.y + sprite.y + localX * s + localY * c;
			const angle = woundAngle(hit.direction);
			direction = sprite.rotation + (sprite.scale.x < 0 ? Math.PI - angle : angle);
		} else {
			x = owner.state.body.x + (key === 'body' ? 0 : owner.state[key].x);
			y = owner.state.body.y + (key === 'body' ? 0 : owner.state[key].y);
			direction = sprite.rotation + Math.PI / 2;
		}
		if (!this.isVisible(x, y)) return;
		for (let i = 0; i < count; i++) {
			const angle = direction + (Math.random() - 0.5) * (count > 2 ? 0.9 : 0.25);
			const speed = (220 + Math.random() * 300) * Math.min(1, wound.life * 2);
			this.spawn(owner, 'particle.png', 'blood', x, y, Math.cos(angle) * speed, Math.sin(angle) * speed);
		}
	}

	recycle(index) {
		const p = this.active[index];
		p.batch.removeParticle(p);
		p.batch = null;
		p.owner = null;
		this.active[index] = this.active[this.active.length - 1];
		this.active.pop();
		this.free.push(p);
	}

	recycleWound(index) {
		const wound = this.wounds[index];
		wound.owner = null;
		this.wounds[index] = this.wounds[this.wounds.length - 1];
		this.wounds.pop();
		this.freeWounds.push(wound);
	}

	endFrame() {
		const dt = this.delta;
		// Reverse traversal also avoids updating droplets newly spawned by chunks.
		for (let i = this.active.length - 1; i >= 0; i--) {
			const p = this.active[i];
			p.age += dt;
			if (p.age >= p.life || !this.isVisible(p.x, p.y)) {
				this.recycle(i);
				continue;
			}
			if (p.kind !== 'cloud') {
				p.x += p.vx * dt + this.gravityX * dt * dt * 0.5;
				p.y += p.vy * dt + this.gravityY * dt * dt * 0.5;
				p.vx += this.gravityX * dt;
				p.vy += this.gravityY * dt;
			} else {
				p.scaleX = p.scaleY = 0.4 + p.age * 2;
			}
			p.rotation += p.spin * dt;
			if (p.kind === 'blood') {
				p.rotation = Math.atan2(p.vy, p.vx);
				p.scaleX = p.baseScale * (1 + Math.min(2, Math.hypot(p.vx, p.vy) / 350));
				p.scaleY = p.baseScale * 0.8;
			}
			p.alpha = Math.min(1, (p.life - p.age) / (p.life * 0.4)) * (p.kind === 'cloud' ? 0.7 : 1);
			if (p.kind === 'chunk' && p.age < 0.45 && p.age >= p.trailAt) {
				p.trailAt = p.age + 0.09;
				this.spawn(p.owner, 'particle.png', 'blood', p.x, p.y, p.vx * 0.25, p.vy * 0.25);
			}
		}
		for (let i = this.wounds.length - 1; i >= 0; i--) {
			const w = this.wounds[i];
			const state = w.owner.state[w.key];
			w.life -= dt;
			w.timer -= dt;
			if (w.life <= 0 || state.destroyed || w.owner.sprite.destroyed) {
				this.recycleWound(i);
				continue;
			}
			if (w.timer > 0) continue;
			w.timer = 0.025;
			this.emitSpray(w, 2);
		}
	}

	removeOwner(owner) {
		for (let i = this.active.length - 1; i >= 0; i--) if (this.active[i].owner === owner) this.recycle(i);
		for (let i = this.wounds.length - 1; i >= 0; i--) if (this.wounds[i].owner === owner) this.recycleWound(i);
	}

	clear() {
		while (this.active.length) this.recycle(this.active.length - 1);
		while (this.wounds.length) this.recycleWound(this.wounds.length - 1);
		for (const batch of this.batches.values()) batch.destroy();
		this.batches.clear();
		this.textures.clear();
		this.tokens = REMOTE_GORE_LIMITS.perFrame;
		this.parent = null;
		this.enabled = false;
	}
}

export const remoteGore = new RemoteGore();
